import { nowEpochMilliseconds } from "@nijiurachan/js/util/time";
import type { QueryKey } from "@tanstack/react-query";
import { focusManager } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type { SortType } from "@/features/catalog/stores/catalogStore";
import { getDocumentVisibilityState } from "@/shared/hooks/useDocumentVisibilityState";

export const CATALOG_AUTO_RELOAD_INTERVAL = 60_000;
export const CATALOG_BACKGROUND_AUTO_RELOAD_INTERVAL = 5 * 60_000;

export const CATALOG_QUERY_BEHAVIOR = {
  staleTime: 30_000,
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: true,
} as const;

export function getCatalogRefetchInterval(
  enabled: boolean,
  visibilityState: DocumentVisibilityState,
): number | false {
  if (!enabled) return false;
  return visibilityState === "visible"
    ? CATALOG_AUTO_RELOAD_INTERVAL
    : CATALOG_BACKGROUND_AUTO_RELOAD_INTERVAL;
}

type CatalogAutoReloadQueryBehavior = Omit<
  typeof CATALOG_QUERY_BEHAVIOR,
  "refetchOnWindowFocus"
> & {
  refetchOnWindowFocus: boolean;
  refetchInterval: (() => number | false) | false;
};

export function createCatalogAutoReloadQueryBehavior(
  enabled: boolean,
  readVisibilityState: () => DocumentVisibilityState = getDocumentVisibilityState,
  randomSort: boolean = false,
): CatalogAutoReloadQueryBehavior {
  return {
    ...CATALOG_QUERY_BEHAVIOR,
    refetchOnWindowFocus: !randomSort,
    refetchInterval: randomSort
      ? false
      : (): number | false =>
          getCatalogRefetchInterval(enabled, readVisibilityState()),
  };
}

/** random sortでは同じseedを使った再取得を避け、次のquery keyへ切り替える。 */
export function refreshCatalogSeedForSort(
  sort: SortType,
  regenerateSeed: () => void,
): boolean {
  if (sort !== "random") return false;
  regenerateSeed();
  return true;
}

/** sort・pageなどを維持したままseedだけが変わったqueryかを判定する。 */
export function isRandomSeedOnlyQueryChange(
  previousQueryKey: QueryKey | undefined,
  currentQueryKey: QueryKey,
  previousPage?: number,
  currentPage?: number,
): boolean {
  if (
    !previousQueryKey ||
    previousQueryKey.length !== currentQueryKey.length ||
    previousQueryKey[0] !== "threads" ||
    currentQueryKey[0] !== "threads" ||
    (previousPage !== undefined && previousPage !== currentPage)
  ) {
    return false;
  }

  const previousPathIndex = previousQueryKey[1] === "infinite" ? 2 : 1;
  const currentPathIndex = currentQueryKey[1] === "infinite" ? 2 : 1;
  if (previousPathIndex !== currentPathIndex) return false;

  const previousPath = previousQueryKey[previousPathIndex];
  const currentPath = currentQueryKey[currentPathIndex];
  if (typeof previousPath !== "string" || typeof currentPath !== "string") {
    return false;
  }

  const previousIdentity = getRandomSeedPathIdentity(previousPath);
  const currentIdentity = getRandomSeedPathIdentity(currentPath);
  if (
    !previousIdentity ||
    !currentIdentity ||
    previousIdentity.isMix !== currentIdentity.isMix ||
    previousIdentity.path !== currentIdentity.path
  ) {
    return false;
  }

  const seedChanged = previousIdentity.seed !== currentIdentity.seed;
  const mixRoundChanged = previousIdentity.round !== currentIdentity.round;
  if (!seedChanged && !mixRoundChanged) return false;
  if (
    mixRoundChanged &&
    (previousPage === undefined ||
      currentPage === undefined ||
      previousPage !== currentPage)
  ) {
    return false;
  }

  return previousQueryKey.every(
    (part, index) =>
      index === previousPathIndex || part === currentQueryKey[index],
  );
}

function getRandomSeedPathIdentity(
  path: string,
): { path: string; seed: string; isMix: boolean; round?: string } | undefined {
  const queryIndex = path.indexOf("?");
  if (queryIndex === -1) return undefined;

  const pathPrefix = path.slice(0, queryIndex);
  const isMix = pathPrefix.startsWith("mix:");
  const queryParts = path.slice(queryIndex + 1).split("&");
  const sortParts = queryParts.filter((part) => part === "sort=random");
  const seedParts = queryParts.filter((part) => part.startsWith("seed="));
  const roundParts = isMix
    ? queryParts.filter((part) => part.startsWith("round="))
    : [];
  const seed = seedParts[0]?.slice("seed=".length);
  const round = roundParts[0]?.slice("round=".length);
  if (
    sortParts.length !== 1 ||
    seedParts.length !== 1 ||
    !seed ||
    !/^\d+$/u.test(seed) ||
    (isMix &&
      (roundParts.length !== 1 || round === undefined || !/^\d+$/u.test(round)))
  ) {
    return undefined;
  }

  const normalizedQuery = queryParts
    .filter(
      (part) =>
        !part.startsWith("seed=") && !(isMix && part.startsWith("round=")),
    )
    .join("&");
  return {
    path: `${pathPrefix}?${normalizedQuery}`,
    seed,
    isMix,
    ...(round === undefined ? {} : { round }),
  };
}

export function shouldReseedCatalogOnFocus(
  dataUpdatedAt: number,
  now: number,
  isFetching: boolean,
  queryStatus: "error" | "pending" | "success" = "success",
): boolean {
  return (
    !isFetching &&
    (queryStatus === "error" ||
      (dataUpdatedAt > 0 &&
        now - dataUpdatedAt >= CATALOG_QUERY_BEHAVIOR.staleTime))
  );
}

export function shouldReseedCatalogOnInterval(
  isFetching: boolean,
  fetchStatus: "fetching" | "paused" | "idle",
): boolean {
  return !isFetching && fetchStatus === "idle";
}

/** Seedはquery keyに残し、オンライン人数の初回取得判定からだけ除く。 */
export function getCatalogFetchContextPath(path: string): string {
  const queryIndex = path.indexOf("?");
  if (queryIndex === -1) return path;

  const queryParts = path
    .slice(queryIndex + 1)
    .split("&")
    .filter((part) => !part.startsWith("seed="));
  return queryParts.length === 0
    ? path.slice(0, queryIndex)
    : `${path.slice(0, queryIndex)}?${queryParts.join("&")}`;
}

interface CatalogRandomSeedReloadQueryState {
  dataUpdatedAt: number;
  fetchStatus: "fetching" | "paused" | "idle";
  isFetching: boolean;
  isPlaceholderData: boolean;
  status: "error" | "pending" | "success";
}

/** random sortのfocus復帰・自動更新をseed更新として行う。 */
export function useCatalogRandomSeedReload(
  enabled: boolean,
  sort: SortType,
  autoReload: boolean,
  regenerateSeed: () => void,
  visibilityState: DocumentVisibilityState,
  queryKey: QueryKey,
  queryState: CatalogRandomSeedReloadQueryState,
  displayPage: number,
): () => Promise<void> {
  const queryKeyRef = useRef(queryKey);
  const queryStateRef = useRef(queryState);
  const regenerateSeedRef = useRef(regenerateSeed);
  const pendingReloadRef = useRef<{
    previousQueryKey: QueryKey;
    previousDisplayPage: number;
    resolve: () => void;
  } | null>(null);

  useEffect(() => {
    queryKeyRef.current = queryKey;
    queryStateRef.current = queryState;
    regenerateSeedRef.current = regenerateSeed;
  }, [queryKey, queryState, regenerateSeed]);

  const settlePendingReload = useCallback((): void => {
    const pendingReload = pendingReloadRef.current;
    if (!pendingReload) return;
    pendingReloadRef.current = null;
    pendingReload.resolve();
  }, []);

  useEffect(() => {
    if (!enabled || sort !== "random") return;

    return focusManager.subscribe(() => {
      if (!focusManager.isFocused()) return;
      const current = queryStateRef.current;
      if (
        shouldReseedCatalogOnFocus(
          current.dataUpdatedAt,
          nowEpochMilliseconds(),
          current.isFetching || current.fetchStatus !== "idle",
          current.status,
        )
      ) {
        refreshCatalogSeedForSort(sort, regenerateSeedRef.current);
      }
    });
  }, [enabled, sort]);

  useEffect(() => {
    if (!enabled || sort !== "random" || !autoReload) return;
    const intervalDuration = getCatalogRefetchInterval(true, visibilityState);
    if (intervalDuration === false) return;
    const interval = window.setInterval(() => {
      const current = queryStateRef.current;
      if (
        !shouldReseedCatalogOnInterval(current.isFetching, current.fetchStatus)
      )
        return;
      refreshCatalogSeedForSort(sort, regenerateSeedRef.current);
    }, intervalDuration);
    return (): void => window.clearInterval(interval);
  }, [autoReload, enabled, sort, visibilityState]);

  useEffect(() => {
    const pendingReload = pendingReloadRef.current;
    if (!pendingReload) return;
    if (
      isRandomSeedOnlyQueryChange(
        pendingReload.previousQueryKey,
        queryKey,
        pendingReload.previousDisplayPage,
        displayPage,
      )
    ) {
      if (
        queryState.status === "error" ||
        (!queryState.isPlaceholderData &&
          queryState.fetchStatus === "idle" &&
          !queryState.isFetching)
      ) {
        settlePendingReload();
      }
      return;
    }

    const queryKeyChanged =
      pendingReload.previousQueryKey.length !== queryKey.length ||
      pendingReload.previousQueryKey.some(
        (part, index) => part !== queryKey[index],
      );
    if (queryKeyChanged) settlePendingReload();
  }, [displayPage, queryKey, queryState, settlePendingReload]);

  useEffect(() => (): void => settlePendingReload(), [settlePendingReload]);

  return useCallback((): Promise<void> => {
    if (!enabled || sort !== "random") return Promise.resolve();

    const previousQueryKey = queryKeyRef.current;
    const promise = new Promise<void>((resolve) => {
      pendingReloadRef.current?.resolve();
      pendingReloadRef.current = {
        previousQueryKey,
        previousDisplayPage: displayPage,
        resolve,
      };
    });
    refreshCatalogSeedForSort(sort, regenerateSeed);
    return promise;
  }, [displayPage, enabled, regenerateSeed, sort]);
}

export function shouldRefreshCatalogOnlineUsers(
  autoReloadEnabled: boolean,
  isInitialFetch: boolean,
  visibilityState: DocumentVisibilityState,
): boolean {
  return !autoReloadEnabled || isInitialFetch || visibilityState === "visible";
}
